import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import {
    Box,
    Button,
    FormControlLabel,
    Stack,
    Typography,
} from "@mui/material";
import UserStore from "components/Admin/UserManager/UserStore";
import AgentStore from "components/KBo/Stores/AgentStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSwitch from "components/UI/AcxSwitch";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import {
    ApplicationFilterDomain,
    ApplicationFiltersService,
    CreateApplicationFilter,
} from "services/ApplicationFiltersService";
import { AsyncTaskStatus } from "stores/BaseStore";
import {
    ApplicationFilters,
    ApplicationFiltersStore,
    SavedFilter,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import { SparklingGradientMultiStar } from "SvgIcons/SparklingGradientStar";
import {
    areFiltersEqual,
    cadenceOptions,
    SubscriptionCadence,
} from "./helpers";
import {
    generateFilterHtml as generateFilterHtmlUtil,
    generateFilters,
} from "./subscriptionRenderedFilter";

export interface HierarchyPath {
    id: string;
    path: string;
}

type SearchSubscriptionDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    source?: "proactive-insights" | "conversations";
    applicationFiltersStore: ApplicationFiltersStore;
    applicationFilterValuesStore: ApplicationFilterValuesStore;
    currentlyAppliedFilters: ApplicationFilters;
    messageStore: MessageStore;
    selectedSavedFilter?: SavedFilter;
    initialKboAnalysisPrompt?: string;
    kboAnalysisPromptPlaceholder?: string;
    subscribeTitle?: string;
    editTitle?: string;
    filtersHeading?: string;
    dataTestId?: string;
};

const applicationFiltersService = new ApplicationFiltersService();

const SearchSubscriptionDialog: React.FC<SearchSubscriptionDialogProps> = ({
    isOpen,
    onClose,
    source = "conversations",
    applicationFiltersStore,
    applicationFilterValuesStore,
    currentlyAppliedFilters,
    messageStore,
    selectedSavedFilter,
    initialKboAnalysisPrompt,
    kboAnalysisPromptPlaceholder,
    subscribeTitle = "Subscribe to Search",
    editTitle = "Edit Search Subscription",
    filtersHeading = "Applied Filters",
    dataTestId = "subscribe-to-search-modal",
}) => {
    const userStore = useStore(UserStore);
    const agentStore = useStore(AgentStore);
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);

    const [savedFilterName, setSavedFilterName] = useState("");
    const [cadence, setCadence] = useState(SubscriptionCadence.Weekly);
    const [existingCadence, setExistingCadence] = useState<
        SubscriptionCadence | undefined
    >(undefined);
    const [isSaving, setIsSaving] = useState(false);
    const [hierarchyPaths, setHierarchyPaths] = useState<HierarchyPath[]>([]);
    const [generatedFilters, setGeneratedFilters] = useState<React.ReactNode[]>(
        [],
    );
    const [isGeneratingFilters, setIsGeneratingFilters] = useState(false);
    const [isSubscribedToggle, setIsSubscribedToggle] = useState(false);
    const [isKboAnalysisEnabled, setIsKboAnalysisEnabled] = useState(false);
    const [existingIsKboAnalysisEnabled, setExistingIsKboAnalysisEnabled] =
        useState(false);
    const [kboAnalysisPrompt, setKboAnalysisPrompt] = useState("");
    const [existingKboAnalysisPrompt, setExistingKboAnalysisPrompt] =
        useState("");
    const idPrefix = `${source}-`;
    const prefixedCadenceOptions = useMemo(
        () =>
            cadenceOptions.map((option) => ({
                ...option,
                id: `${idPrefix}${option.id}`,
            })),
        [idPrefix],
    );

    const isExistingFilter = Boolean(selectedSavedFilter?.id);
    const isFilterCreator = Boolean(
        selectedSavedFilter?.userId === userStore.appUser?.id,
    );
    // The sanitizer drops Question Result rows this viewer cannot see, so both
    // sides of the comparison must recompute once the module catalog resolves.
    const questionResultModuleAccessKey =
        applicationFilterValuesStore.questionResultModuleAccessKey;
    const sanitizedAppliedFilters = useMemo(() => {
        void questionResultModuleAccessKey;
        return applicationFilterItemsStore.stripInaccessibleFilters(
            currentlyAppliedFilters,
        ).filters;
    }, [
        currentlyAppliedFilters,
        applicationFilterItemsStore,
        questionResultModuleAccessKey,
    ]);

    const isSavedFilterApplied = useMemo(() => {
        void questionResultModuleAccessKey;
        const sanitize = (f?: ApplicationFilters) =>
            f
                ? applicationFilterItemsStore.stripInaccessibleFilters(f)
                      .filters
                : undefined;

        return areFiltersEqual(
            sanitize(selectedSavedFilter?.value),
            sanitize(currentlyAppliedFilters),
        );
    }, [
        selectedSavedFilter?.value,
        currentlyAppliedFilters,
        applicationFilterItemsStore,
        questionResultModuleAccessKey,
    ]);

    const isSubscribedToAppliedFilters =
        !!selectedSavedFilter?.applicationFilterSubscriptionId &&
        isSavedFilterApplied;

    const areCurrentFiltersApplied = true;
    const hasModifiedFilters = true;

    const isUnchangedSubscription =
        isSubscribedToAppliedFilters &&
        cadence === existingCadence &&
        isKboAnalysisEnabled === existingIsKboAnalysisEnabled &&
        kboAnalysisPrompt === existingKboAnalysisPrompt &&
        isSubscribedToggle;

    const hasReachedKboEmailAnalysisLimit =
        !agentStore.hasViewKboPermission &&
        agentStore.hasReachedKboEmailAnalysisLimit &&
        !existingIsKboAnalysisEnabled;
    const shouldShowKboLimitMessage =
        isKboAnalysisEnabled && hasReachedKboEmailAnalysisLimit;
    const isRequestAccessDisabled =
        agentStore.isAccessRequested ||
        agentStore.getTaskLoading(AgentStore.Tasks.RequestAccess);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setIsGeneratingFilters(true);

        void generateFilters({
            filters: sanitizedAppliedFilters,
            applicationFiltersStore,
            applicationFilterValuesStore,
        }).then(({ filterJsx, newHierarchyPaths }) => {
            setGeneratedFilters(filterJsx);
            setHierarchyPaths(newHierarchyPaths);
            setIsGeneratingFilters(false);
        });
    }, [
        isOpen,
        sanitizedAppliedFilters,
        applicationFiltersStore,
        applicationFilterValuesStore,
    ]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setIsSubscribedToggle(isSubscribedToAppliedFilters);
    }, [isOpen, isSubscribedToAppliedFilters]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const defaultPrompt = initialKboAnalysisPrompt ?? "";

        const getSubscription = async () => {
            if (!selectedSavedFilter?.applicationFilterSubscriptionId) {
                return;
            }

            try {
                const subscription =
                    await applicationFiltersService.getApplicationFilterSubscription(
                        selectedSavedFilter.applicationFilterSubscriptionId,
                    );
                if (subscription?.cadence) {
                    setCadence(subscription.cadence);
                    setExistingCadence(subscription.cadence);
                } else {
                    setExistingCadence(undefined);
                }
                setIsKboAnalysisEnabled(
                    subscription?.isKboAnalysisEnabled ?? false,
                );
                setExistingIsKboAnalysisEnabled(
                    subscription?.isKboAnalysisEnabled ?? false,
                );
                setKboAnalysisPrompt(subscription?.kboAnalysisPrompt ?? "");
                setExistingKboAnalysisPrompt(
                    subscription?.kboAnalysisPrompt ?? "",
                );
            } catch {
                messageStore.logError("Failed to load subscription details.");
            }
        };

        setSavedFilterName(selectedSavedFilter?.name || "");

        if (selectedSavedFilter?.applicationFilterSubscriptionId) {
            void getSubscription();
            return;
        }

        setCadence(SubscriptionCadence.Weekly);
        setExistingCadence(undefined);
        setIsKboAnalysisEnabled(false);
        setExistingIsKboAnalysisEnabled(false);
        setKboAnalysisPrompt("");
        setExistingKboAnalysisPrompt(defaultPrompt);
    }, [isOpen, selectedSavedFilter, initialKboAnalysisPrompt, messageStore]);

    const generateFilterHtml = async () => {
        return await generateFilterHtmlUtil({
            filters: sanitizedAppliedFilters,
            fetchedHierarchyPaths: hierarchyPaths,
            applicationFiltersStore,
            applicationFilterValuesStore,
        });
    };

    const getEffectiveKboAnalysisPrompt = () => {
        const trimmedPrompt = kboAnalysisPrompt.trim();
        if (trimmedPrompt) {
            return trimmedPrompt;
        }

        return initialKboAnalysisPrompt?.trim() || null;
    };

    const updateSavedFilterInStore = (updatedFilter: SavedFilter) => {
        const nextSavedFilters = applicationFiltersStore.savedFilters.map(
            (sf) => (sf.id === updatedFilter.id ? updatedFilter : sf),
        );
        applicationFiltersStore.setSavedFilters(nextSavedFilters);
    };

    const updateSavedFilterSubscriptionId = (subscriptionId?: string) => {
        if (!selectedSavedFilter) {
            return;
        }

        updateSavedFilterInStore({
            ...selectedSavedFilter,
            applicationFilterSubscriptionId: subscriptionId,
        });
    };

    const saveFilterAndSubscription = async () => {
        if (shouldShowKboLimitMessage) {
            messageStore.logError(
                "You have reached your limit for KBo email analysis.",
            );
            return;
        }

        const filter: CreateApplicationFilter = {
            name: savedFilterName,
            value: sanitizedAppliedFilters,
            domain: ApplicationFilterDomain.User,
            subscriptionInfo: {
                filterHtml: await generateFilterHtml(),
                cadence,
                name: savedFilterName,
                isKboAnalysisEnabled,
                kboAnalysisPrompt: isKboAnalysisEnabled
                    ? getEffectiveKboAnalysisPrompt()
                    : null,
            },
        };

        if (
            applicationFiltersStore.savedFilters.some(
                (sf) => sf.name === savedFilterName,
            )
        ) {
            messageStore.logError("Filter names must be unique");
            return;
        }

        setIsSaving(true);

        try {
            const response =
                await applicationFiltersService.createApplicationFilterSubscription(
                    filter,
                );
            if (response.id) {
                applicationFiltersStore.addSavedFiltersByDomain(response);
                await agentStore.refreshKboStatus();
                messageStore.logInfo(
                    "Subscribed! You'll receive an email when there are 1 or more new conversations matching your search.",
                );
            }
        } catch {
            setIsSaving(false);
            messageStore.logError("Failed to create subscription.");
            return;
        }

        setIsSaving(false);
        onClose();
    };

    const unsubscribeFromApplicationFilter = async () => {
        if (!selectedSavedFilter?.applicationFilterSubscriptionId) {
            return;
        }

        await applicationFiltersService.unsubscribeFromApplicationFilter(
            selectedSavedFilter.applicationFilterSubscriptionId,
        );
        await agentStore.refreshKboStatus();
        updateSavedFilterSubscriptionId(undefined);
        setIsSaving(false);
        messageStore.logInfo(
            `You have unsubscribed from '${selectedSavedFilter.name}'.`,
        );
        onClose();
        setCadence(SubscriptionCadence.Weekly);
        setExistingCadence(undefined);
        setIsKboAnalysisEnabled(false);
        setExistingIsKboAnalysisEnabled(false);
        setKboAnalysisPrompt("");
        setExistingKboAnalysisPrompt(initialKboAnalysisPrompt ?? "");
    };

    const updateFilterAndSubscription = async () => {
        if (!selectedSavedFilter) {
            return;
        }

        setIsSaving(true);

        if (shouldShowKboLimitMessage) {
            setIsSaving(false);
            messageStore.logError(
                "You have reached your limit for KBo email analysis.",
            );
            return;
        }

        if (selectedSavedFilter.applicationFilterSubscriptionId) {
            if (!isSubscribedToggle) {
                await unsubscribeFromApplicationFilter();
                return;
            }

            if (!isSubscribedToAppliedFilters) {
                const filterInstance = applicationFiltersStore.toInstance();
                filterInstance.applyExternalFilters(sanitizedAppliedFilters);

                const updateFilterResult =
                    await applicationFiltersStore.updateFilters(
                        selectedSavedFilter.id,
                        filterInstance,
                        savedFilterName,
                        selectedSavedFilter.domain,
                        applicationFilterValuesStore,
                        selectedSavedFilter.applicationFilterSubscriptionId,
                    );

                if (updateFilterResult === AsyncTaskStatus.Error) {
                    setIsSaving(false);
                    messageStore.logError("Failed to update filter.");
                    return;
                }
            }

            try {
                const newSubscriptionId =
                    await applicationFiltersService.updateApplicationFilterSubscription(
                        selectedSavedFilter.applicationFilterSubscriptionId,
                        {
                            cadence,
                            isKboAnalysisEnabled,
                            kboAnalysisPrompt: isKboAnalysisEnabled
                                ? getEffectiveKboAnalysisPrompt()
                                : null,
                        },
                    );
                if (newSubscriptionId) {
                    updateSavedFilterSubscriptionId(newSubscriptionId);
                }
                await agentStore.refreshKboStatus();
            } catch {
                setIsSaving(false);
                messageStore.logError("Failed to update subscription.");
                return;
            }

            messageStore.logInfo(
                `'${selectedSavedFilter.name}' subscription updated successfully. `,
            );
        } else {
            try {
                const subscriptionId =
                    await applicationFiltersService.createApplicationFilterSubscriptionFromSavedFilter(
                        {
                            cadence,
                            filterHtml: await generateFilterHtml(),
                            name: savedFilterName,
                            isKboAnalysisEnabled,
                            kboAnalysisPrompt: isKboAnalysisEnabled
                                ? getEffectiveKboAnalysisPrompt()
                                : null,
                        },
                        selectedSavedFilter.id,
                    );
                if (subscriptionId) {
                    updateSavedFilterSubscriptionId(subscriptionId);
                } else {
                    setIsSaving(false);
                    messageStore.logError("Failed to create subscription.");
                    return;
                }
                await agentStore.refreshKboStatus();
            } catch {
                setIsSaving(false);
                messageStore.logError("Failed to create subscription.");
                return;
            }

            messageStore.logInfo(
                "Subscribed! You'll receive an email when there are 1 or more new conversations matching your search.",
            );
        }

        setIsSaving(false);
        onClose();
    };

    const isNotOwnerOfModifiedFilter =
        !isFilterCreator && !!selectedSavedFilter && !isSavedFilterApplied;

    const isSaveDisabled =
        !savedFilterName.trim() ||
        isUnchangedSubscription ||
        isGeneratingFilters ||
        shouldShowKboLimitMessage ||
        !areCurrentFiltersApplied ||
        isNotOwnerOfModifiedFilter ||
        !hasModifiedFilters;

    return (
        <AcxDialog
            isOpen={isOpen}
            onClose={onClose}
            title={isSubscribedToAppliedFilters ? editTitle : subscribeTitle}
            data-testid={dataTestId}
            dialogContentSx={{ padding: 0 }}
            dialogContentChildren={
                isGeneratingFilters ? (
                    <AcxEmptyState size="large" variant="loading" />
                ) : (
                    <Stack rowGap={2} paddingBlock={3} paddingTop={0}>
                        <Typography marginInline={3}>
                            You&apos;ll only receive an email if there are 1 or
                            more new conversations matching your search.
                        </Typography>
                        <Box
                            sx={{
                                borderRadius: 2,
                                marginInline: 3,
                                p: 2,
                                background: theme.palette.gray[100],
                            }}
                        >
                            <Typography sx={{ fontWeight: "semibold" }}>
                                {filtersHeading}
                            </Typography>
                            <Stack
                                component="ul"
                                spacing={0.5}
                                sx={{
                                    maxHeight: "min(400px, 30vh)",
                                    overflowY: "auto",
                                }}
                            >
                                {generatedFilters}
                            </Stack>
                        </Box>
                        <AcxMainTextField
                            id="subscription-saved-filter-name"
                            labelText="Saved Filter Name"
                            placeholderText="Enter..."
                            value={savedFilterName}
                            onChange={(e) => setSavedFilterName(e.target.value)}
                            isDisabled={isExistingFilter}
                            fullWidth
                            containerStyle={{
                                marginInline: theme.spacing(3),
                            }}
                        />
                        <span style={{ marginInline: theme.spacing(3) }}>
                            <AcxSelectSingle
                                options={prefixedCadenceOptions}
                                defaultValue={prefixedCadenceOptions.find(
                                    (option) => option.value === cadence,
                                )}
                                id="subscription-cadence"
                                valueField="value"
                                labelField="label"
                                inputLabel="Frequency"
                                fullWidth
                                onChange={(option) => setCadence(option.value)}
                            />
                        </span>
                        {isSubscribedToAppliedFilters && (
                            <FormControlLabel
                                sx={{ marginInline: 3 }}
                                control={
                                    <AcxSwitch
                                        sx={{ marginLeft: 0 }}
                                        id="subscribe-to-search-toggle"
                                        checked={isSubscribedToggle}
                                        onChange={() =>
                                            setIsSubscribedToggle(
                                                !isSubscribedToggle,
                                            )
                                        }
                                    />
                                }
                                label="Subscription Notification"
                            />
                        )}
                        <Stack
                            sx={{
                                background:
                                    "linear-gradient(95deg, #1D2A60 19.4%, #239C98 134.79%)",
                                color: "white !important",
                            }}
                            rowGap={1}
                            paddingTop={1.5}
                            paddingBottom={2}
                        >
                            <FormControlLabel
                                sx={{ marginInline: 3 }}
                                control={
                                    <AcxSwitch
                                        sx={{
                                            marginLeft: 0,
                                            "& .MuiSwitch-thumb": {
                                                backgroundColor:
                                                    "white !important",
                                            },
                                        }}
                                        id={`${idPrefix}kbo-analysis-toggle`}
                                        checked={isKboAnalysisEnabled}
                                        onChange={() =>
                                            setIsKboAnalysisEnabled(
                                                !isKboAnalysisEnabled,
                                            )
                                        }
                                    />
                                }
                                label={
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        columnGap={1}
                                    >
                                        <Typography
                                            color="white"
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 600,
                                                lineHeight: "20px",
                                                color: "white !important",
                                            }}
                                        >
                                            Try KBo Analysis via email
                                        </Typography>
                                        <SparklingGradientMultiStar />
                                    </Stack>
                                }
                            />
                            {shouldShowKboLimitMessage && (
                                <Stack sx={{ marginInline: 3 }}>
                                    <Typography
                                        sx={{
                                            color: "white !important",
                                            fontSize: "14px",
                                            lineHeight: "20px",
                                        }}
                                    >
                                        Heads up! You have reached your limit
                                        for KBo email analysis. Unlock full KBo
                                        capabilities and receive an analysis of
                                        this search.
                                    </Typography>
                                    <Button
                                        disableRipple
                                        fullWidth={false}
                                        disabled={isRequestAccessDisabled}
                                        onClick={() =>
                                            agentStore.requestAccess()
                                        }
                                        endIcon={
                                            !agentStore.isAccessRequested ? (
                                                <ArrowForwardRoundedIcon />
                                            ) : undefined
                                        }
                                        sx={{
                                            alignSelf: "flex-start",
                                            color: "white !important",
                                            fontSize: "14px",
                                            fontWeight: 700,
                                            lineHeight: "20px",
                                            minWidth: 0,
                                            padding: 0,
                                            textTransform: "none",
                                            "& .MuiButton-endIcon": {
                                                marginLeft: 0.5,
                                            },
                                            "&.Mui-disabled": {
                                                color: "rgba(255,255,255,0.65) !important",
                                            },
                                        }}
                                    >
                                        {agentStore.isAccessRequested
                                            ? "We&apos;ll be in touch shortly!"
                                            : "Get in touch with Authenticx"}
                                    </Button>
                                </Stack>
                            )}
                            {isKboAnalysisEnabled &&
                                !shouldShowKboLimitMessage && (
                                    <AcxMainTextField
                                        id="kbo-analysis-prompt"
                                        labelText={`Describe why these conversations are relevant, and what you hope to learn. Our AI assistant, KBo, will deliver a ${SubscriptionCadence[
                                            cadence
                                        ]
                                            .toString()
                                            .toLowerCase()} email analysis of new conversations matching your search.`}
                                        labelSxImportant={{
                                            "&.Mui-focused": {
                                                color: "white !important",
                                            },
                                            color: "white !important",
                                            fontWeight: "400 !important",
                                            whiteSpace: "pre-wrap",
                                        }}
                                        placeholderText={
                                            kboAnalysisPromptPlaceholder ??
                                            "Example: What is driving the increased Eddy rate on prior authorization with this patient group?"
                                        }
                                        value={kboAnalysisPrompt}
                                        onChange={(e) =>
                                            setKboAnalysisPrompt(e.target.value)
                                        }
                                        fullWidth
                                        multiline
                                        rows={3}
                                        containerStyle={{
                                            marginInline: theme.spacing(3),
                                        }}
                                    />
                                )}
                        </Stack>
                    </Stack>
                )
            }
            cancelButtonId="subscribe-to-search-cancel"
        >
            <AcxButton
                disabled={isSaveDisabled}
                loading={isSaving}
                onClick={
                    isExistingFilter
                        ? updateFilterAndSubscription
                        : saveFilterAndSubscription
                }
                id={`${idPrefix}subscribe-to-search-save`}
            >
                Save Subscription
            </AcxButton>
        </AcxDialog>
    );
};

export default observer(SearchSubscriptionDialog);
